/**
 * http.js — shared fetch wrapper for the customer portal.
 *
 * This app has no axios instance/interceptor (see notifications.api.js), so every
 * call site built its own `fetch(...)` and had no way to notice an expired session
 * except a generic "request failed" message. `apiFetch` has the exact same
 * signature as `fetch` — same args, same Response return — so callers only need to
 * swap the function they call; response.ok/response.json() handling elsewhere is
 * unaffected.
 *
 * On a 401 it clears the portal session and hard-redirects to /login, mirroring
 * the existing forced-logout path in App.js (handleForceLogout: clear session,
 * then navigate away) so an expired/invalid token behaves the same whether the
 * server pushes a socket event or simply rejects the next request.
 */

export const logout = () => {
  localStorage.removeItem("portalToken");
  localStorage.removeItem("portalUser");

  // Guard against a redirect loop: a 401 from a request fired while already on
  // /login (e.g. a stale background poll) would otherwise reload indefinitely.
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

export const apiFetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401) {
    logout();
  }
  return response;
};

// Same 401 handling for the handful of call sites still using axios directly
// instead of apiFetch (e.g. RoomNumberPageAero.jsx's map-loading calls).
export const withAuthErrorHandling = async (axiosCall) => {
  try {
    return await axiosCall();
  } catch (error) {
    if (error?.response?.status === 401) {
      logout();
    }
    throw error;
  }
};

export default apiFetch;
